import { VBCContactInfo } from '../VBCContactActions';
import { VBCQRCode } from '../VBCQRCode';

export interface VBCContactBarProps {
  // Contact data
  contactName?: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  phone: string | null;
  cellPhone: string | null;
  website: string | null;
  // Vendor identity
  vendorId: string | number;
  /**
   * Full vCard 3.0 text the QR encodes. When set, a QR section renders below
   * the contact info inside the left card. Null/undefined hides the QR
   * entirely. Phones recognize the embedded vCard payload on scan and prompt
   * to save the contact natively — no browser detour, no server hit.
   */
  vcardContent?: string | null;
  // Callback passthrough for interactive components
  onTrackInteraction?: (type: string, vendorId: string | number) => void;
  // Right column slot — replaces the default Actions section
  actionsSlot?: React.ReactNode;
}

/**
 * Contact info + right column layout (side by side on desktop).
 * Left: VBCContactInfo (+ optional QR code beneath). Right: actionsSlot.
 */
export function VBCContactBar({
  contactName,
  address,
  city,
  state,
  zipCode,
  phone,
  cellPhone,
  website,
  vendorId,
  vcardContent,
  onTrackInteraction,
  actionsSlot,
}: VBCContactBarProps) {
  return (
    <div className="mb-10 grid gap-6 md:grid-cols-2">
      {/* Contact info */}
      <section
        className="flex flex-col rounded-xl border p-6"
        style={{
          borderColor: 'var(--brand-card-border)',
          boxShadow: 'var(--shadow-resting)',
          backgroundColor: 'var(--brand-card-bg)',
        }}
      >
        <h2
          className="overline mb-4"
          style={{ color: 'var(--brand-primary, #014F8A)' }}
        >
          Contact Information
        </h2>
        <VBCContactInfo
          contactName={contactName}
          address={address}
          city={city}
          state={state}
          zipCode={zipCode}
          phone={phone}
          cellPhone={cellPhone}
          website={website}
          vendorId={vendorId}
          onTrackInteraction={onTrackInteraction}
        />
        {vcardContent && (
          // mt-auto anchors the QR to the bottom of the card. The outer grid's
          // default `align-items: stretch` makes both columns the same height,
          // so when the right column (inquiry form) is taller, the QR drops to
          // the bottom-left corner. pt-6 keeps a minimum gap when content is
          // tall enough to push the QR up against the contact info.
          <div className="mt-auto pt-6">
            <VBCQRCode vcardContent={vcardContent} />
          </div>
        )}
      </section>

      {/* Right column — app-provided content (e.g. inquiry form) */}
      {actionsSlot}
    </div>
  );
}
